export const DEFAULT_SCAN_OPTIONS = {
  bufferTimeoutMs: 120,
  duplicateScanWindowMs: 500,
  terminatorKeys: ['Enter', 'Tab', 'LineFeed'],
};

const ZULAY_ORGANIZATION_IDS = [
  '68fa76c859faf810db8f4c1f', // Zulay Kitchen
  '69120e80aaa6bb8405355eaa', // Zulay Kitchen (Returns)
  '699710918a5ff72c8c02c178', // Zulay Kitchen Demo
];

const safeText = value => (value == null ? '' : String(value));

export const redactBarcode = value => {
  const text = safeText(value);

  return {
    length: text.length,
    last4: text.slice(-4),
  };
};

export const shouldEnableScannerDiagnostics = ({ currentOrg, user } = {}) => {
  const organizationId = safeText(currentOrg?._id || user?.organizationId);

  return ZULAY_ORGANIZATION_IDS.includes(organizationId);
};

export const createScannerDiagnostics = ({
  enabled = false,
  sessionId = `scanner-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  context = {},
  upload,
  maxEvents = 300,
  now = () => Date.now(),
} = {}) => {
  const events = [];
  let sequence = 0;
  let lastUploadAt = null;

  const record = (type, payload = {}) => {
    if (!enabled) return;

    events.push({
      type,
      sequence: (sequence += 1),
      timestamp: new Date(now()).toISOString(),
      route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      ...payload,
    });

    if (events.length > maxEvents) {
      events.splice(0, events.length - maxEvents);
    }
  };

  const snapshot = () => ({
    sessionId,
    context,
    events: [...events],
  });

  const flush = async (reason, extra = {}) => {
    if (!enabled || !upload || events.length === 0) return;

    const currentTime = now();
    if (lastUploadAt !== null && currentTime - lastUploadAt < 5000 && reason !== 'manual')
      return;

    lastUploadAt = currentTime;

    try {
      const outgoing = snapshot();
      await upload({
        ...outgoing,
        reason,
        extra,
      });
      events.length = 0;
    } catch {
      // Diagnostics must never break packing.
    }
  };

  return {
    enabled,
    sessionId,
    record,
    snapshot,
    flush,
  };
};

const describeTarget = target => {
  if (!target) return null;

  return {
    tagName: target.tagName,
    type: target.type,
    id: target.id,
    name: target.name,
    className:
      typeof target.className === 'string' ? target.className.slice(0, 120) : undefined,
  };
};

export const createBarcodeScannerHandler = ({
  onScanBarcode,
  shouldIgnoreScan = () => false,
  diagnostics,
  options = {},
  now = () => Date.now(),
}) => {
  const settings = { ...DEFAULT_SCAN_OPTIONS, ...options };
  let scannedBarcode = '';
  let lastInputAt = 0;
  let lastScan = null;

  return event => {
    const eventTime = now();
    const key = event?.key;
    const hasCommandModifier = !!(event?.ctrlKey || event?.altKey || event?.metaKey);
    const isTerminator = settings.terminatorKeys.includes(key);

    diagnostics?.record('keydown', {
      code: event?.code,
      keyCode: event?.keyCode,
      which: event?.which,
      repeat: event?.repeat,
      ctrlKey: event?.ctrlKey,
      altKey: event?.altKey,
      shiftKey: event?.shiftKey,
      metaKey: event?.metaKey,
      isComposing: event?.isComposing,
      target: describeTarget(event?.target),
      activeElement:
        typeof document !== 'undefined' ? describeTarget(document.activeElement) : null,
      bufferLength: scannedBarcode.length,
      deltaMs: lastInputAt ? eventTime - lastInputAt : null,
    });

    if (isTerminator) {
      event?.preventDefault?.();
      event?.stopPropagation?.();

      if (!scannedBarcode) {
        diagnostics?.record('ignored-empty-terminator');
        return;
      }

      const code = scannedBarcode;
      scannedBarcode = '';

      if (shouldIgnoreScan(code)) {
        diagnostics?.record('ignored-scan', {
          reason: 'disabled-or-processing',
          barcode: redactBarcode(code),
        });
        diagnostics?.flush?.('ignored-scan', { barcode: redactBarcode(code) });
        return;
      }

      if (
        lastScan?.code === code &&
        eventTime - lastScan.timestamp < settings.duplicateScanWindowMs
      ) {
        diagnostics?.record('ignored-scan', {
          reason: 'duplicate-window',
          deltaMs: eventTime - lastScan.timestamp,
          barcode: redactBarcode(code),
        });
        diagnostics?.flush?.('duplicate-scan', { barcode: redactBarcode(code) });
        return;
      }

      lastScan = { code, timestamp: eventTime };
      diagnostics?.record('completed-scan', { barcode: redactBarcode(code) });
      onScanBarcode(code);
      return;
    }

    if (!hasCommandModifier && key?.length === 1) {
      if (lastInputAt && eventTime - lastInputAt > settings.bufferTimeoutMs) {
        diagnostics?.record('buffer-timeout-reset', {
          previousLength: scannedBarcode.length,
          deltaMs: eventTime - lastInputAt,
        });
        scannedBarcode = '';
      }

      scannedBarcode += key;
      lastInputAt = eventTime;
    }
  };
};
